'use client';

/**
 * Global command palette — Spotlight / VS Code style.
 *
 * Triggered by ⌘K / Ctrl+K from anywhere (KeyboardShortcuts.tsx dispatches
 * the `cmdpalette:open` CustomEvent). Offers:
 *
 *   - Static commands (navigate, create, toggle theme, switch language, logout)
 *   - Dynamic prompt search via `api.prompts.list({ search })`
 *   - Per-item icons, keyboard-only navigation (↑ ↓ Enter Esc)
 *   - Backdrop blur + centered modal card
 *
 * Kept intentionally light — no external command-palette library. The hook
 * graph is small enough that cmd+k feels instant even on a cold cache.
 */

import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import { useI18n } from '@/lib/i18n';
import { Prompt } from '@/types';

export const CMD_OPEN_EVENT = 'cmdpalette:open';

type StaticItem = {
  id: string;
  label: string;
  hint?: string;
  icon: string;
  keywords: string;       // flat text for fuzzy matching
  run: () => void;
  section: 'Navigate' | 'Create' | 'Appearance' | 'Account';
};

type PromptItem = {
  id: string;
  label: string;
  subtitle?: string;
  icon: string;
  run: () => void;
  section: 'Prompts';
};

type Item = StaticItem | PromptItem;

// ── Fuzzy scorer: subsequence match + bonus for word-boundary hits ──
// Not the fanciest algorithm but more than good enough for a few dozen
// candidates; returns a positive score (bigger = better) or 0 on miss.
function fuzzyScore(haystack: string, needle: string): number {
  if (!needle) return 1;              // empty query matches everything
  const h = haystack.toLowerCase();
  const n = needle.toLowerCase();
  let hi = 0, score = 0, inRun = false, lastMatchIdx = -2;
  for (let ni = 0; ni < n.length; ni++) {
    while (hi < h.length && h[hi] !== n[ni]) hi++;
    if (hi >= h.length) return 0;
    // Bonuses
    if (hi === 0 || h[hi - 1] === ' ' || h[hi - 1] === '/') score += 4;  // word start
    if (lastMatchIdx + 1 === hi) { score += 3; inRun = true; } else inRun = false;
    score += 1;
    lastMatchIdx = hi;
    hi++;
  }
  // Shorter strings score higher (e.g. "home" beats "home page heading").
  return score + Math.max(0, 16 - h.length) / 16;
}

export default function CommandPalette() {
  const router = useRouter();
  const { user, logout } = useAuth();
  const { locale, setLocale } = useI18n();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [promptResults, setPromptResults] = useState<Prompt[]>([]);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const searchDebounce = useRef<number | null>(null);

  // Subscribe to the global open event.
  useEffect(() => {
    const onOpen = () => {
      setOpen(true);
      setQuery('');
      setActive(0);
      setTimeout(() => inputRef.current?.focus(), 0);
    };
    window.addEventListener(CMD_OPEN_EVENT, onOpen);
    return () => window.removeEventListener(CMD_OPEN_EVENT, onOpen);
  }, []);

  // Close on Escape anywhere in the modal.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); setOpen(false); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Debounced prompt search — fires only when query looks like it's for prompts.
  useEffect(() => {
    if (!open) return;
    if (searchDebounce.current) window.clearTimeout(searchDebounce.current);
    // Very short queries → skip the API call; static commands cover them.
    if (query.trim().length < 2) { setPromptResults([]); return; }
    searchDebounce.current = window.setTimeout(() => {
      api.prompts.list({ search: query, size: 6 })
        .then((r) => setPromptResults(r.items))
        .catch(() => setPromptResults([]));
    }, 180);
    return () => {
      if (searchDebounce.current) window.clearTimeout(searchDebounce.current);
    };
  }, [query, open]);

  // ── Static commands ─────────────────────────────────────────
  const staticItems = useMemo<StaticItem[]>(() => {
    const nav = (href: string) => () => router.push(href);
    const base: StaticItem[] = [
      { id: 'go:home',     section: 'Navigate',   icon: 'home',         label: 'Go to Home',         keywords: 'home gallery',               run: nav('/') },
      { id: 'go:explore',  section: 'Navigate',   icon: 'explore',      label: 'Explore categories', keywords: 'explore categories craft browse', run: nav('/explore') },
      { id: 'go:trending', section: 'Navigate',   icon: 'local_fire_department', label: 'Go to Trending', keywords: 'trending hot',     run: nav('/trending') },
      { id: 'go:collections', section: 'Navigate', icon: 'library_books', label: 'Browse collections', keywords: 'collections playlists folders packs', run: nav('/collections') },
      { id: 'go:tags',     section: 'Navigate',   icon: 'sell',         label: 'Browse tags',        keywords: 'tags categories topics',     run: nav('/tags') },
      // Theme toggle removed — site is light-only.
      { id: 'appearance:lang-en', section: 'Appearance', icon: 'language', label: 'Language · English', keywords: 'english en locale language', run: () => setLocale('en') },
      { id: 'appearance:lang-th', section: 'Appearance', icon: 'language', label: 'Language · ไทย',     keywords: 'thai th ภาษา locale', run: () => setLocale('th') },
    ];
    if (user) {
      base.push(
        { id: 'create',       section: 'Create',   icon: 'add',          label: 'New prompt',         keywords: 'create write new add share',        run: nav('/create') },
        { id: 'go:following', section: 'Navigate', icon: 'group',        label: 'Go to Following feed', keywords: 'following feed', run: nav('/following') },
        { id: 'go:saved',     section: 'Navigate', icon: 'bookmark',     label: 'Your saved prompts', keywords: 'saved bookmarks library',           run: nav('/my/saved') },
        { id: 'go:my',        section: 'Navigate', icon: 'grid_view',    label: 'Your prompts',       keywords: 'my prompts my library',             run: nav('/my/prompts') },
        { id: 'go:profile',   section: 'Account',  icon: 'person',       label: 'Your profile',       keywords: `profile ${user.username}`,           run: nav(`/profile/${user.username}`) },
        { id: 'account:logout', section: 'Account', icon: 'logout',       label: 'Logout',             keywords: 'logout signout signoff',            run: () => { logout(); router.push('/'); } },
      );
      // Admin console isn't surfaced in the user palette anymore — it's a
      // separate app at /admin/login with its own credentials.
    } else {
      base.push({ id: 'account:login', section: 'Account', icon: 'login', label: 'Sign in', keywords: 'login signin auth', run: nav('/auth/login') });
    }
    return base;
  }, [router, user, logout, setLocale]);

  // ── Rank + merge candidates ─────────────────────────────────
  const ranked = useMemo<Item[]>(() => {
    const q = query.trim();
    const staticScored = staticItems
      .map((it) => ({ it, s: fuzzyScore(`${it.label} ${it.keywords}`, q) }))
      .filter((x) => x.s > 0)
      .sort((a, b) => b.s - a.s)
      .map((x) => x.it);

    const promptItems: PromptItem[] = promptResults.map((p) => ({
      id: `prompt:${p.id}`,
      section: 'Prompts',
      icon: p.cover_image_url ? 'image' : 'article',
      label: p.title,
      subtitle: `@${p.user.username}${p.category ? ` · ${p.category}` : ''}`,
      run: () => router.push(`/prompt/${p.id}`),
    }));

    // Static commands first (keyboard-heavy users want them near the top),
    // then prompts by recency.
    return [...staticScored, ...promptItems];
  }, [staticItems, promptResults, query, router]);

  // Clamp active index when the list shrinks.
  useEffect(() => {
    setActive((i) => Math.min(i, Math.max(0, ranked.length - 1)));
  }, [ranked.length]);

  // Scroll active item into view.
  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector(`[data-idx="${active}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const run = useCallback((idx: number) => {
    const it = ranked[idx];
    if (!it) return;
    setOpen(false);
    // Tiny delay so the modal close transition doesn't stutter the nav.
    setTimeout(() => it.run(), 0);
  }, [ranked]);

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(ranked.length - 1, i + 1)); }
    else if (e.key === 'ArrowUp')   { e.preventDefault(); setActive((i) => Math.max(0, i - 1)); }
    else if (e.key === 'Enter')     { e.preventDefault(); run(active); }
    else if (e.key === 'Tab')       { e.preventDefault(); /* block focus leak */ }
  };

  // Group items by section for display. Group ORDER is first-occurrence in
  // the ranked list (so the highest-ranked Navigate item carries the whole
  // Navigate group to the top); items within a group preserve their rank.
  // We use a Map to consolidate non-consecutive runs — without it, ranking
  // could produce two "Navigate" groups → duplicate React keys.
  const grouped = useMemo(() => {
    const map = new Map<string, { section: string; items: { item: Item; idx: number }[] }>();
    ranked.forEach((item, idx) => {
      let g = map.get(item.section);
      if (!g) {
        g = { section: item.section, items: [] };
        map.set(item.section, g);
      }
      g.items.push({ item, idx });
    });
    return [...map.values()];
  }, [ranked]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
      onClick={() => setOpen(false)}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'color-mix(in oklch, var(--bg) 45%, transparent)',
        backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        paddingTop: 'min(20vh, 140px)',
        animation: 'cmd-fade-in 120ms ease-out',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        onKeyDown={onKey}
        style={{
          width: 'min(640px, calc(100vw - 32px))',
          maxHeight: '70vh',
          display: 'flex', flexDirection: 'column',
          background: 'var(--bg-raised)',
          border: '1px solid var(--line)',
          borderRadius: 14,
          boxShadow: '0 24px 64px -20px rgba(0,0,0,0.35), 0 8px 20px -8px rgba(0,0,0,0.15)',
          overflow: 'hidden',
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '14px 18px', borderBottom: '1px solid var(--line)',
        }}>
          <span className="material-symbols-outlined" style={{ color: 'var(--ink-3)', fontSize: 20 }}>
            search
          </span>
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setActive(0); }}
            placeholder="Type a command or search prompts…"
            style={{
              flex: 1, background: 'transparent', border: 'none', outline: 'none',
              color: 'var(--ink)', fontSize: 16, fontFamily: 'var(--font-sans)',
            }}
            autoFocus
          />
          <kbd style={{
            fontSize: 10, padding: '3px 6px',
            background: 'var(--bg-soft)', border: '1px solid var(--line)',
            borderRadius: 4, color: 'var(--ink-3)', fontFamily: 'var(--font-mono)',
          }}>
            ESC
          </kbd>
        </div>

        <div ref={listRef} style={{ overflowY: 'auto', flex: 1, padding: '8px 0' }}>
          {ranked.length === 0 ? (
            <div style={{
              padding: '36px 24px', textAlign: 'center',
              color: 'var(--ink-3)', fontSize: 13,
            }}>
              No matches — try different keywords.
            </div>
          ) : (
            grouped.map((group) => (
              <div key={group.section} style={{ padding: '6px 0' }}>
                <div style={{
                  fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.12em',
                  color: 'var(--ink-4)', fontWeight: 600,
                  padding: '4px 18px 6px',
                }}>
                  {group.section}
                </div>
                {group.items.map(({ item, idx }) => (
                  <button
                    key={item.id}
                    data-idx={idx}
                    onMouseEnter={() => setActive(idx)}
                    onClick={() => run(idx)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      width: '100%', padding: '10px 18px',
                      background: idx === active ? 'var(--bg-soft)' : 'transparent',
                      border: 'none', cursor: 'pointer',
                      color: 'var(--ink)', fontSize: 14,
                      fontFamily: 'var(--font-sans)', textAlign: 'left',
                      transition: 'background 0.08s',
                    }}
                  >
                    <span className="material-symbols-outlined" style={{
                      fontSize: 18, color: idx === active ? 'var(--accent)' : 'var(--ink-3)',
                      flex: 'none',
                    }}>
                      {item.icon}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {item.label}
                      </div>
                      {'subtitle' in item && item.subtitle && (
                        <div style={{
                          fontSize: 11.5, color: 'var(--ink-3)',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                          {item.subtitle}
                        </div>
                      )}
                    </div>
                    {idx === active && (
                      <span className="material-symbols-outlined"
                        style={{ fontSize: 14, color: 'var(--ink-3)', flex: 'none' }}>
                        keyboard_return
                      </span>
                    )}
                  </button>
                ))}
              </div>
            ))
          )}
        </div>

        <div style={{
          display: 'flex', alignItems: 'center', gap: 16,
          padding: '8px 18px', borderTop: '1px solid var(--line)',
          background: 'var(--bg-soft)',
          fontSize: 10.5, color: 'var(--ink-4)',
        }}>
          <span><kbd style={kbdStyle}>↑</kbd> <kbd style={kbdStyle}>↓</kbd> navigate</span>
          <span><kbd style={kbdStyle}>⏎</kbd> select</span>
          <span><kbd style={kbdStyle}>esc</kbd> close</span>
          <span style={{ marginLeft: 'auto', letterSpacing: '0.08em' }}>
            Active locale: {locale.toUpperCase()}
          </span>
        </div>
      </div>

      <style>{`@keyframes cmd-fade-in { from { opacity: 0; } to { opacity: 1; } }`}</style>
    </div>,
    document.body
  );
}

const kbdStyle: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 10,
  padding: '2px 5px',
  background: 'var(--bg-raised)',
  border: '1px solid var(--line)',
  borderRadius: 3,
  color: 'var(--ink-3)',
};
